import _ from "lodash";
import React from "react";
import ReactDOMServer from "react-dom/server";
import { useI18n } from "Hooks";

// design system
import { IconButton, Tooltip } from "@toddle-edu/ds-web";
import {
  PlayCircleOutlined,
  AttachmentClipOutlined,
  PencilLineOutlined,
  CloseOutlined,
} from "@toddle-edu/ds-icons";

// utils
import { getPromptLabelsObj } from "Utils/AIUtils";
import { onEnterOrSpaceKeyDown } from "Utils";
import { getEmojisAndTextFromString } from "Utils/StringHelpers";

// constants
import { EMOJI_COLOR_TRAY } from "../../modules/Constants";

// third party
import classNames from "classnames";

// scss
import classes from "./PromptCards.scss";

const iconProps = {
  variant: "link",
  size: "xxx-small",
};

// helper function to render the icon as an HTML string
const renderPrefixIcon = ({ showEditIcon }) => {
  return ReactDOMServer.renderToString(
    <span className={classes.iconContainer}>
      {showEditIcon ? (
        <PencilLineOutlined {...iconProps} />
      ) : (
        <AttachmentClipOutlined {...iconProps} />
      )}
    </span>
  );
};

const renderSuffixIcon = () => {
  return ReactDOMServer.renderToString(
    <span className={classes.suffixIconContainer} id="data-suffix-icon">
      <CloseOutlined {...iconProps} />
    </span>
  );
};

// replace text wrapped with # or ## with the appropriate spans
const tooltipOffset = { offset: [-10, 0] };

const styles = {
  overlayInnerStyle: {
    maxWidth: "220px",
  },
};

// replace text wrapped with # with span with contentEditable attribute
export const wrapPlaceholderWithSpan = params => {
  const {
    label,
    showEditIcon,
    attachmentId,
    removeRightMargin = false,
    isLocalizedGoalMismatch = false,
  } = params;

  let newLabel = label;
  if (isLocalizedGoalMismatch) {
    newLabel = `<span style="color: red;">${label}</span>`;
  }
  // regex to match both #...# and ##...##
  const placeholderPattern = /(##|#)\s*(.*?)\s*\1/g;

  // replace placeholders based on their type
  const updatedLabel = _.replace(
    newLabel,
    placeholderPattern,
    (match, hashes, content, offset, string) => {
      // Check if the next character after the match is a full stop
      const isFollowedByPeriod = string[offset + match.length] === ".";

      const applyZeroRightMargin = removeRightMargin || isFollowedByPeriod;

      if (hashes === "#") {
        const placeholderClasses = classNames(classes.placeholderSpan, {
          [classes.marginRightZero]: applyZeroRightMargin,
        });

        // for editable placeholder
        const uniqueId = _.uniqueId("placeholder-");
        return `<span id="content-editable-placeholder" data-placeholder-id="${uniqueId}" data-placeholder="${content}" class="${placeholderClasses}" role="textbox" contenteditable="true"></span>`;
      } else if (hashes === "##") {
        // for non-editable, clickable placeholder with icon
        const prefixIconHTML = renderPrefixIcon({ showEditIcon });
        const suffixIconHTML = renderSuffixIcon();

        // don't change 'attachment-content' id as it is being used in 'onAttachmentsUpdate' func in AIContextBar
        const contentWithIcon = `${prefixIconHTML} ${`<span id="attachment-content" data-role="attachment-content${
          attachmentId ? "-ellipsis" : ""
        }" role="button" contenteditable="false">${content}</span>`} ${
          showEditIcon ? suffixIconHTML : ""
        }`.trim();

        const attachmentButtonContainerClasses = classNames(
          classes.attachmentButtonContainer,
          {
            [classes.marginRightZero]: applyZeroRightMargin,
          }
        );

        return `<span id="attachment-text" data-attachment-id="${attachmentId}" class="${attachmentButtonContainerClasses}" role="button" contenteditable="false">
                      ${contentWithIcon}
                  </span>`;
      }
    }
  );

  return updatedLabel;
};

const PromptCards = props => {
  const {
    prompts,
    allPrompts,
    selectedPrompt,
    setSelectedPrompt,
    setSelectedAction,
    source,
    updatePromptSuggestionModalInfo,
  } = props;

  const i18n = useI18n(["common", "ai", "classRoom"]);
  const { t } = i18n;

  // functions

  const onClickPrompt = params => {
    const { prompt } = params;
    const { metadata } = prompt || {};
    const { tutorBehaviour } = metadata || {};

    const {
      tutorGoal,
      tutorSummary,
      isLocalizedGoalMismatch,
    } = getPromptLabelsObj({
      ...i18n,
      ...metadata,
      ...prompt,
    });

    if (setSelectedPrompt) setSelectedPrompt(prompt);

    const childPromptId = _.get(prompt, "children.0");
    const childPrompt = _.find(allPrompts, { id: childPromptId });

    if (childPromptId) {
      const label = wrapPlaceholderWithSpan({
        t,
        label: tutorGoal,
        isLocalizedGoalMismatch,
      });
      setSelectedAction({
        ...(childPrompt
          ? childPrompt
          : { action: "draft_ai_tutor", fieldType: "BRAINSTORM" }),
        label: label,
        displayText: label,
        subtext: tutorSummary,
        tutorBehaviour,
      });
    }
  };

  const onClickInfoIcon = prompt => {
    updatePromptSuggestionModalInfo({
      showModal: true,
      params: { id: prompt?.id },
    });
  };

  const isLibrary = source === "LIBRARY";

  // classes

  const containerClass = classNames({
    [classes.container]: true,
    [classes.libraryContainer]: isLibrary,
  });

  return (
    <div className={containerClass}>
      {_.map(prompts, prompt => {
        const { id, children, metadata = {} } = prompt || {};

        const {
          displayText: label,
          tutorSummary: subtext,
          isLocalizedLabelMismatch,
          isLocalizedSubtextMismatch,
        } = getPromptLabelsObj({
          ...i18n,
          ...metadata,
          ...prompt,
        });

        if (!label || _.isEmpty(children)) return null;

        const { emoji, text } = getEmojisAndTextFromString(label) || {};

        const backgroundColor = EMOJI_COLOR_TRAY[id % _.size(EMOJI_COLOR_TRAY)];

        const promptClass = classNames(classes.prompt, {
          [classes.selectedPrompt]: prompt?.id === selectedPrompt?.id,
        });

        const promptTitleClass = classNames(
          "text-body-2",
          classes.promptTitle,
          {
            [classes.errorText]: isLocalizedLabelMismatch,
          }
        );

        const promptDescriptionClass = classNames(
          "text-body-2",
          classes.promptDescription,
          {
            [classes.errorText]: isLocalizedSubtextMismatch,
          }
        );

        return (
          <div
            id={`${source}_PROMPT_${prompt?.id}`}
            role={"button"}
            key={prompt?.id}
            className={promptClass}
            tabIndex={0}
            onClick={() => onClickPrompt({ prompt })}
            onKeyDown={e =>
              onEnterOrSpaceKeyDown(e, () => onClickPrompt({ prompt }))
            }
          >
            {!isLibrary ? (
              <div className={classes.infoIcon}>
                <Tooltip
                  dsVersion={"2.0"}
                  tooltip={t("ai:preview_this_tutor_template")}
                  placement={"top"}
                  showArrow={true}
                >
                  <IconButton
                    dsVersion={"2.0"}
                    size={"medium"}
                    variant={"neutral"}
                    type={"plain"}
                    icon={<PlayCircleOutlined />}
                    onClick={e => {
                      e.stopPropagation();
                      onClickInfoIcon(prompt);
                    }}
                  />
                </Tooltip>
              </div>
            ) : null}

            <div className={classes.emojiContainer} style={{ backgroundColor }}>
              {emoji || "📚"}
            </div>
            <div className={promptTitleClass}>{text}</div>
            <Tooltip
              dsVersion={"2.0"}
              showArrow={true}
              tooltip={subtext}
              visible={_.size(subtext) > 130}
              placement={"right"}
              overlayInnerStyle={styles.overlayInnerStyle}
              align={tooltipOffset}
            >
              <div className={promptDescriptionClass}>{subtext}</div>
            </Tooltip>
          </div>
        );
      })}
    </div>
  );
};

export default PromptCards;
